import { MAP_INK, MAP_INK_SOFT, MAP_SELECTED } from "./mapPalette";
import { useEffect, useState } from "react";
import { useRef } from "react";
import maplibregl, {
  ExpressionSpecification,
  GeoJSONSource,
  MapLayerMouseEvent,
  StyleSpecification,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Box } from "@mui/material";
import type { FeatureCollection, Point } from "geojson";

import { ClusterBadge, computeClusterBadges } from "./clusterBadges";
import { ColorRamp, GeoMapCanvasProps, GeoMapPoint, SizeRamp } from "./types";

const SOURCE_ID = "geomap-points";
const LAYER_POINTS = "geomap-points-unclustered";
const LAYER_HIGHLIGHT = "geomap-points-highlight";
const LAYER_POINTS_CASING = "geomap-points-casing";
const LAYER_SUBJECT = "geomap-points-subject";
const LAYER_SELECTED = "geomap-points-selected";
const AREA_SOURCE_ID = "geomap-areas";
const LAYER_AREAS = "geomap-areas-fill";
const LAYER_AREA_OUTLINE = "geomap-areas-outline";
const LAYER_AREA_HIGHLIGHT = "geomap-areas-highlight";

const EMPTY_COLLECTION: GeoJSON.FeatureCollection = {
  type: "FeatureCollection",
  features: [],
};

// A filter no feature matches — the highlight layer's idle state.
const MATCH_NOTHING: ExpressionSpecification = ["==", ["get", "id"], -1];

/**
 * True once `map.remove()` has run. During unmount/hot-reload commits,
 * sibling effects can still fire with the previous (now removed) instance
 * before the `setMap(null)` re-render lands; any style access on it throws.
 * MapLibre exposes no public flag, so read its private `_removed`.
 */
function isRemoved(map: maplibregl.Map): boolean {
  return (map as unknown as { _removed?: boolean })._removed === true;
}

const POINT_RADIUS = 8;

/** How far the light casing extends past the point, and the highlight past that. */
const CASING_WIDTH = 2.5;
const HIGHLIGHT_GAP = 5;

/**
 * Point radius, from the ramp when one is given.
 *
 * ⚠️ Every layer that has to line up with a point derives its radius from this one
 * expression — the casing under it, the ring around the highlighted one, the subject
 * marker. Repeating the interpolation per layer is how they drift apart the first
 * time a stop moves.
 */
const radiusExpression = (
  sizeRamp: SizeRamp | undefined
): number | ExpressionSpecification => {
  if (!sizeRamp || sizeRamp.stops.length < 2) return POINT_RADIUS;
  const stops = sizeRamp.stops.flatMap(([value, radius]) => [value, radius]);
  return [
    "interpolate",
    ["linear"],
    ["get", "value"],
    ...stops,
  ] as ExpressionSpecification;
};

const grownBy = (
  radius: number | ExpressionSpecification,
  amount: number
): number | ExpressionSpecification =>
  typeof radius === "number"
    ? radius + amount
    : (["+", radius, amount] as ExpressionSpecification);

interface PointProperties {
  id: number;
  label: string;
  value: number;
}

function toFeatureCollection(
  points: readonly GeoMapPoint[]
): FeatureCollection<Point, PointProperties> {
  return {
    type: "FeatureCollection",
    features: points.map((point) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [point.longitude, point.latitude],
      },
      properties: { id: point.id, label: point.label, value: point.value },
    })),
  };
}

/** MapLibre piecewise-linear color expression equivalent to `colorForValue`. */
function rampExpression(
  ramp: ColorRamp,
  input: ExpressionSpecification
): ExpressionSpecification {
  const expression: unknown[] = ["interpolate", ["linear"], input];
  ramp.stops.forEach(([value, color]) => expression.push(value, color));
  return expression as unknown as ExpressionSpecification;
}

/** Pixels of breathing room the auto-fit leaves around the outermost points. */
const FIT_PADDING = 60;

/**
 * How close the auto-fit may go.
 *
 * ⚠️ Reached by a *single* point, not by a wide set: with nothing to bound, the fit
 * runs to the ceiling. That is why a lone property pin lands at exactly this zoom.
 */
const FIT_MAX_ZOOM = 12;

/**
 * Interactive map canvas (pan / zoom / scroll) rendering value-colored
 * points with count-badged clusters.
 *
 * Points are colored through `colorRamp`; clusters through the ramp applied
 * to their average value. Clicking a cluster zooms to its expansion level;
 * hover and click on single points are reported by point id, so the caller
 * owns tooltip content and click semantics.
 *
 * Cluster badges are DOM markers rather than a MapLibre symbol layer: text
 * layers need a glyph server, which the authenticated tile proxy does not
 * expose, while DOM markers render text natively (and inherit the app font).
 */
export const GeoMapCanvas = ({
  points,
  areas,
  colorRamp,
  sizeRamp,
  subjectPointId,
  selectedPointIds,
  selectedColor = MAP_SELECTED,
  labelFromZoom,
  maxLabels = 12,
  tileUrlTemplate,
  attribution,
  transformRequest,
  onPointHover,
  onPointClick,
  highlightedPointId,
  autoFitKey,
  fitPadding = FIT_PADDING,
  fitMaxZoom = FIT_MAX_ZOOM,
  fitMinZoom,
}: GeoMapCanvasProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<maplibregl.Map | null>(null);
  const [hasAutoFitted, setHasAutoFitted] = useState(false);
  // Last viewport, carried across map recreations (e.g. the tile style
  // changing on a theme switch) so the user does not lose their position.
  // A ref, not state: it must never trigger a render or re-create the map.
  const viewportRef = useRef<{
    center: maplibregl.LngLat;
    zoom: number;
  } | null>(null);

  // ── Map lifecycle ────────────────────────────────────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const style: StyleSpecification = {
      version: 8,
      sources: {
        basemap: {
          type: "raster",
          tiles: [tileUrlTemplate],
          tileSize: 256,
          attribution,
        },
      },
      layers: [{ id: "basemap", type: "raster", source: "basemap" }],
    };

    const carried = viewportRef.current;
    const instance = new maplibregl.Map({
      container,
      style,
      ...(carried && { center: carried.center, zoom: carried.zoom }),
      attributionControl: { compact: true },
      transformRequest: transformRequest
        ? (url) => transformRequest(url)
        : undefined,
    });
    instance.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      "top-right"
    );

    instance.on("load", () => {
      // Choropleth areas, beneath the point layers.
      instance.addSource(AREA_SOURCE_ID, {
        type: "geojson",
        data: EMPTY_COLLECTION,
      });
      instance.addLayer({
        id: LAYER_AREAS,
        type: "fill",
        source: AREA_SOURCE_ID,
        paint: { "fill-opacity": 0.75 },
      });
      instance.addLayer({
        id: LAYER_AREA_OUTLINE,
        type: "line",
        source: AREA_SOURCE_ID,
        paint: {
          "line-color": "rgba(255, 255, 255, 0.9)",
          "line-width": 1,
        },
      });
      instance.addLayer({
        id: LAYER_AREA_HIGHLIGHT,
        type: "line",
        source: AREA_SOURCE_ID,
        filter: MATCH_NOTHING,
        paint: {
          "line-color": "rgba(255, 255, 255, 1)",
          "line-width": 3,
        },
      });

      instance.addSource(SOURCE_ID, {
        type: "geojson",
        data: toFeatureCollection([]),
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 45,
        clusterProperties: { valueSum: ["+", ["get", "value"]] },
      });
      // ⚠️ Two tones, on two layers, because a circle has one stroke. A single
      // white outline vanishes against a pale basemap and a single dark one against
      // a dark basemap; a light casing *under* a dark-stroked point reads on both,
      // and this map's tiles are not ours to choose.
      instance.addLayer({
        id: LAYER_POINTS_CASING,
        type: "circle",
        source: SOURCE_ID,
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-radius": POINT_RADIUS + CASING_WIDTH,
          "circle-color": "rgba(255, 255, 255, 0.92)",
        },
      });
      instance.addLayer({
        id: LAYER_POINTS,
        type: "circle",
        source: SOURCE_ID,
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-radius": POINT_RADIUS,
          "circle-opacity": 0.95,
          "circle-stroke-width": 1,
          "circle-stroke-color": "rgba(15, 23, 42, 0.55)",
        },
      });
      // Between the points and the subject: a chosen point overrides its ramp
      // colour, and the subject overrides everything.
      instance.addLayer({
        id: LAYER_SELECTED,
        type: "circle",
        source: SOURCE_ID,
        filter: MATCH_NOTHING,
        paint: {
          "circle-radius": POINT_RADIUS,
          "circle-opacity": 0.95,
          "circle-stroke-width": 2.5,
          "circle-stroke-color": "rgba(15, 23, 42, 0.75)",
        },
      });
      // ⚠️ The subject drawn as a ring, not a filled circle: it used to be
      // distinguished only by sitting at the far end of the colour ramp, which makes
      // it one more dark dot in a dark cluster.
      instance.addLayer({
        id: LAYER_SUBJECT,
        type: "circle",
        source: SOURCE_ID,
        filter: MATCH_NOTHING,
        paint: {
          "circle-radius": POINT_RADIUS + 1,
          "circle-color": "rgba(255, 255, 255, 0.95)",
          "circle-stroke-width": 4,
          "circle-stroke-color": MAP_INK_SOFT,
        },
      });
      // Ring drawn around the highlighted point (empty filter = no ring).
      //
      // ⚠️ Dark, where it used to be white — the same colour as the casing beneath
      // it, so on a light basemap the one state that has to be unmistakable was the
      // one that disappeared.
      instance.addLayer({
        id: LAYER_HIGHLIGHT,
        type: "circle",
        source: SOURCE_ID,
        filter: MATCH_NOTHING,
        paint: {
          "circle-radius": POINT_RADIUS + HIGHLIGHT_GAP,
          "circle-color": "rgba(0, 0, 0, 0)",
          "circle-stroke-width": 3,
          "circle-stroke-color": "rgba(15, 23, 42, 0.9)",
        },
      });
      setMap(instance);
    });

    return () => {
      // Capture the viewport before teardown; `hasAutoFitted` is left as-is
      // so a recreation restores this viewport instead of refitting.
      viewportRef.current = {
        center: instance.getCenter(),
        zoom: instance.getZoom(),
      };
      setMap(null);
      instance.remove();
    };
  }, [tileUrlTemplate, attribution, transformRequest]);

  // ── Data + paint updates ─────────────────────────────────────────────────
  useEffect(() => {
    if (!map || isRemoved(map)) return;
    const source = map.getSource<GeoJSONSource>(SOURCE_ID);
    source?.setData(toFeatureCollection(points));
  }, [map, points]);

  useEffect(() => {
    if (!map || isRemoved(map)) return;
    const source = map.getSource<GeoJSONSource>(AREA_SOURCE_ID);
    source?.setData(areas ?? EMPTY_COLLECTION);
  }, [map, areas]);

  useEffect(() => {
    if (!map || isRemoved(map)) return;
    const color = rampExpression(colorRamp, [
      "get",
      "value",
    ] as ExpressionSpecification);
    map.setPaintProperty(LAYER_POINTS, "circle-color", color);
    map.setPaintProperty(LAYER_AREAS, "fill-color", color);
  }, [map, colorRamp]);

  useEffect(() => {
    if (!map || isRemoved(map)) return;
    const radius = radiusExpression(sizeRamp);
    map.setPaintProperty(LAYER_POINTS, "circle-radius", radius);
    map.setPaintProperty(
      LAYER_POINTS_CASING,
      "circle-radius",
      grownBy(radius, CASING_WIDTH)
    );
    map.setPaintProperty(LAYER_SELECTED, "circle-radius", radius);
    map.setPaintProperty(
      LAYER_SUBJECT,
      "circle-radius",
      grownBy(radius, CASING_WIDTH)
    );
    map.setPaintProperty(
      LAYER_HIGHLIGHT,
      "circle-radius",
      grownBy(radius, HIGHLIGHT_GAP)
    );
  }, [map, sizeRamp]);

  useEffect(() => {
    if (!map || isRemoved(map)) return;
    map.setFilter(
      LAYER_SUBJECT,
      subjectPointId === null || subjectPointId === undefined
        ? MATCH_NOTHING
        : (["==", ["get", "id"], subjectPointId] as ExpressionSpecification)
    );
  }, [map, subjectPointId]);

  useEffect(() => {
    if (!map || isRemoved(map)) return;
    map.setFilter(
      LAYER_SELECTED,
      !selectedPointIds || selectedPointIds.length === 0
        ? MATCH_NOTHING
        : ([
            "in",
            ["get", "id"],
            ["literal", [...selectedPointIds]],
          ] as ExpressionSpecification)
    );
    map.setPaintProperty(LAYER_SELECTED, "circle-color", selectedColor);
  }, [map, selectedPointIds, selectedColor]);

  useEffect(() => {
    if (!map || isRemoved(map)) return;
    const filter =
      highlightedPointId === null || highlightedPointId === undefined
        ? MATCH_NOTHING
        : ([
            "==",
            ["get", "id"],
            highlightedPointId,
          ] as ExpressionSpecification);
    map.setFilter(LAYER_HIGHLIGHT, filter);
    map.setFilter(LAYER_AREA_HIGHLIGHT, filter);
  }, [map, highlightedPointId]);

  // ── Initial framing ──────────────────────────────────────────────────────
  // ⚠️ Opt-in, and it resets the latch rather than fitting directly, so the fit runs
  // through the one code path below.
  //
  // The latch clears on every *change* of the key, and the effect below then fits on
  // the next non-empty set — whatever that happens to contain. It cannot tell a
  // complete set from a half-loaded one, so a caller whose points arrive in steps
  // must withhold the key (pass `undefined`) until they are all in.
  useEffect(() => {
    if (autoFitKey === undefined) return;
    setHasAutoFitted(false);
  }, [autoFitKey]);

  useEffect(() => {
    if (!map || isRemoved(map) || hasAutoFitted) return;
    if (points.length === 0 && !areas?.features.length) return;
    const bounds = new maplibregl.LngLatBounds();
    points.forEach((point) => bounds.extend([point.longitude, point.latitude]));
    // Area geometries: walk the raw coordinate arrays (any nesting depth).
    const extend = (coords: unknown): void => {
      if (!Array.isArray(coords)) return;
      if (
        coords.length >= 2 &&
        typeof coords[0] === "number" &&
        typeof coords[1] === "number"
      ) {
        bounds.extend([coords[0], coords[1]]);
        return;
      }
      coords.forEach(extend);
    };
    areas?.features.forEach((feature) => {
      if ("coordinates" in feature.geometry)
        extend(feature.geometry.coordinates);
    });
    map.fitBounds(bounds, {
      padding: fitPadding,
      maxZoom: fitMaxZoom,
      duration: 0,
    });
    // ⚠️ Clamped after the fit, because `fitBounds` has no floor — it takes a
    // `maxZoom` and nothing for the other end. Over a wide radius the bounds pull
    // the view out to the whole city, and no amount of padding brings it back.
    if (fitMinZoom !== undefined && map.getZoom() < fitMinZoom) {
      map.setZoom(fitMinZoom);
    }
    setHasAutoFitted(true);
  }, [map, points, areas, hasAutoFitted, fitPadding, fitMaxZoom, fitMinZoom]);

  // ── Point labels (DOM markers, for want of a glyph source) ───────────────
  //
  // ⚠️ Not a `symbol` layer with `text-field`. The style is a bare raster basemap
  // with no `glyphs` endpoint, so a text layer renders nothing at all — silently,
  // which is the worst way for it to fail. DOM markers need no font atlas and reuse
  // the pattern the cluster badges already established here.
  //
  // ⚠️ The cost of leaving the engine is collision detection: maplibre would drop
  // overlapping labels for free, and nothing here can. `maxLabels` stands in for it
  // — past that many visible points the labels would be a grey smear, so there are
  // none. Blunt, but it fails toward legibility.
  useEffect(() => {
    if (!map || isRemoved(map) || labelFromZoom === undefined) return;

    const labels = new Map<number, maplibregl.Marker>();

    const clear = () => {
      labels.forEach((marker) => marker.remove());
      labels.clear();
    };

    const sync = () => {
      if (!map.getSource(SOURCE_ID)) return;
      if (map.getZoom() < labelFromZoom) {
        clear();
        return;
      }
      if (!map.getLayer(LAYER_POINTS)) return;
      // ⚠️ `queryRenderedFeatures`, not `querySourceFeatures`. The source answers from
      // every *loaded tile*, which reaches beyond the viewport — so `maxLabels` was
      // counting points nobody can see and blanking the labels on a map showing four
      // pins, and markers were being built for off-screen coordinates. Rendered
      // features are the ones on screen, which is the population this guard is about.
      const visible = map
        .queryRenderedFeatures({ layers: [LAYER_POINTS] })
        .filter((feature) => !feature.properties?.point_count);
      // Still deduplicated by id: both queries return one copy per tile, so a point on
      // a tile seam would otherwise be labelled twice, half-overlapping.
      const unique = new Map<
        number,
        { label: string; lngLat: [number, number] }
      >();
      visible.forEach((feature) => {
        const id = feature.properties?.id;
        const label = feature.properties?.label;
        if (typeof id !== "number" || typeof label !== "string") return;
        if (feature.geometry.type !== "Point") return;
        const [longitude, latitude] = feature.geometry.coordinates as number[];
        if (longitude === undefined || latitude === undefined) return;
        unique.set(id, { label, lngLat: [longitude, latitude] });
      });

      if (unique.size > maxLabels) {
        clear();
        return;
      }

      labels.forEach((marker, id) => {
        if (!unique.has(id)) {
          marker.remove();
          labels.delete(id);
        }
      });
      unique.forEach((entry, id) => {
        const existing = labels.get(id);
        if (existing) {
          existing.setLngLat(entry.lngLat);
          existing.getElement().textContent = entry.label;
          return;
        }
        const element = document.createElement("div");
        element.textContent = entry.label;
        Object.assign(element.style, {
          // Halo rather than a solid plate: a box would hide the pins behind it,
          // and this has to sit over an arbitrary basemap.
          color: MAP_INK,
          textShadow: "0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff",
          font: "600 12px system-ui, sans-serif",
          maxWidth: "160px",
          textAlign: "center",
          pointerEvents: "none",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        });
        labels.set(
          id,
          new maplibregl.Marker({ element, offset: [0, -18] })
            .setLngLat(entry.lngLat)
            .addTo(map)
        );
      });
    };

    sync();
    map.on("moveend", sync);
    map.on("sourcedata", sync);
    return () => {
      map.off("moveend", sync);
      map.off("sourcedata", sync);
      clear();
    };
  }, [map, labelFromZoom, maxLabels]);

  // ── Cluster badges (DOM markers diffed against computeClusterBadges) ─────
  useEffect(() => {
    if (!map || isRemoved(map)) return;

    const markers = new Map<number, maplibregl.Marker>();

    const zoomIntoCluster = (clusterId: number, center: [number, number]) => {
      const source = map.getSource<GeoJSONSource>(SOURCE_ID);
      source
        ?.getClusterExpansionZoom(clusterId)
        .then((zoom) => map.easeTo({ center, zoom }))
        .catch(() => undefined);
    };

    const applyBadgeStyle = (element: HTMLElement, badge: ClusterBadge) => {
      element.textContent = badge.countLabel;
      Object.assign(element.style, {
        width: `${badge.size}px`,
        height: `${badge.size}px`,
        background: badge.color,
      });
    };

    const sync = () => {
      if (!map.getSource(SOURCE_ID)) return;
      const badges = computeClusterBadges(
        map.querySourceFeatures(SOURCE_ID),
        colorRamp
      );

      markers.forEach((marker, clusterId) => {
        if (!badges.has(clusterId)) {
          marker.remove();
          markers.delete(clusterId);
        }
      });

      badges.forEach((badge, clusterId) => {
        const existing = markers.get(clusterId);
        if (existing) {
          existing.setLngLat(badge.lngLat);
          applyBadgeStyle(existing.getElement(), badge);
          return;
        }

        const element = document.createElement("div");
        Object.assign(element.style, {
          borderRadius: "50%",
          border: "2px solid rgba(255, 255, 255, 0.85)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: "600",
          cursor: "pointer",
        });
        applyBadgeStyle(element, badge);
        element.addEventListener("click", (event) => {
          event.stopPropagation();
          zoomIntoCluster(clusterId, badge.lngLat);
        });

        markers.set(
          clusterId,
          new maplibregl.Marker({ element }).setLngLat(badge.lngLat).addTo(map)
        );
      });
    };

    // moveend covers pan/zoom (clusters merge/split); sourcedata covers
    // setData refreshes and tiles arriving. Cheaper than per-frame "render".
    map.on("moveend", sync);
    map.on("sourcedata", sync);
    sync();
    return () => {
      map.off("moveend", sync);
      map.off("sourcedata", sync);
      markers.forEach((marker) => marker.remove());
    };
  }, [map, colorRamp]);

  // ── Point interactions ───────────────────────────────────────────────────
  useEffect(() => {
    if (!map || isRemoved(map)) return;

    const pointId = (event: MapLayerMouseEvent): number | null => {
      const id = event.features?.[0]?.properties?.id;
      return typeof id === "number" ? id : null;
    };

    const handleMove = (event: MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = "pointer";
      onPointHover?.(pointId(event), { x: event.point.x, y: event.point.y });
    };
    const handleLeave = () => {
      map.getCanvas().style.cursor = "";
      onPointHover?.(null, null);
    };
    const handleClick = (event: MapLayerMouseEvent) => {
      const id = pointId(event);
      if (id !== null)
        onPointClick?.(id, { x: event.point.x, y: event.point.y });
    };

    // Same contract for points and areas: callbacks receive properties.id.
    const layers = [LAYER_POINTS, LAYER_AREAS];
    layers.forEach((layer) => {
      map.on("mousemove", layer, handleMove);
      map.on("mouseleave", layer, handleLeave);
      map.on("click", layer, handleClick);
    });
    return () => {
      layers.forEach((layer) => {
        map.off("mousemove", layer, handleMove);
        map.off("mouseleave", layer, handleLeave);
        map.off("click", layer, handleClick);
      });
    };
  }, [map, onPointHover, onPointClick]);

  return (
    <Box
      ref={containerRef}
      sx={(theme) => ({
        width: "100%",
        height: "100%",
        borderRadius: 1,
        overflow: "hidden",
        // Map chrome follows the app theme: MapLibre's CSS pins its own
        // font, and its controls/attribution ship as white surfaces.
        "& .maplibregl-map": { fontFamily: "inherit" },
        "& .maplibregl-ctrl-group, & .maplibregl-ctrl-attrib": {
          backgroundColor: theme.vars?.palette.background.paper,
        },
        "& .maplibregl-ctrl-attrib a": {
          color: theme.vars?.palette.text.secondary,
        },
        // The control icons are dark inline SVGs; invert them on the dark
        // scheme so they stay visible on the paper surface.
        ...theme.applyStyles("dark", {
          "& .maplibregl-ctrl-icon": { filter: "invert(1)" },
        }),
      })}
    />
  );
};
